// ── NotificationPrompt.jsx ────────────────────────────────────────────────────
// مكون يشجع المستخدم على تفعيل الإشعارات عند دخول الموقع
// - يظهر بعد 4 ثوانٍ لجميع المستخدمين (مسجّلين وزوار) بعد اختيار اللغة
// - لا يظهر إذا كانت الإشعارات مفعّلة بالفعل
// - إذا كانت الإشعارات محظورة من المتصفح:
//     * يعرض تعليمات رفع الحظر
//     * بعد إعادة التحميل يُفتح تلقائياً تخصيص الإشعارات
// - يعرض تخصيص الإشعارات (أيها تريد تلقّيه) مع تمييز ما يتطلب تسجيل دخول
// - يحترم خيار "لا تظهر مرة أخرى" ويحفظه في localStorage
// - يعيد الظهور كل 3 أيام إذا لم يُغلق بـ "لا تظهر مرة أخرى"
// - يُزامن التفضيلات مع الـ Service Worker فور منح الإذن

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Bell, X, CheckCircle, Settings, Shield,
  Lock, Users, UserCheck, HeadphonesIcon, Globe,
  Info, RefreshCw, ShoppingCart,
} from "lucide-react";
import { getFCMToken, registerFCMServiceWorker } from "../firebase";
import { ref, set } from "firebase/database";
import { db } from "../firebase";
import {
  NOTIF_TYPES, getNotifPrefs, saveNotifPrefs, syncPrefsWithSW,
} from "../hooks/useFCM";
import "./NotificationPrompt.css";

const STORAGE_KEY   = "notif_prompt_dismissed"; // "forever" | timestamp
const SNOOZE_DAYS   = 3;
const SHOW_DELAY_MS = 4000;

// ── أنواع الإشعارات وتسمياتها ──────────────────────────────────────────────────
const NOTIF_ITEMS = [
  {
    type:   NOTIF_TYPES.GLOBAL_CHAT,
    labelAr: "الشات العام",
    labelEn: "Global Chat",
    descAr:  "رسائل الشات العام والردود",
    descEn:  "Global chat messages & replies",
    icon:    Globe,
    color:   "#e50000",
    requiresAuth: false,
  },
  {
    type:   NOTIF_TYPES.PRIVATE_MSG,
    labelAr: "الرسائل الخاصة",
    labelEn: "Private Messages",
    descAr:  "رسائل المحادثات الخاصة",
    descEn:  "Private chat messages",
    icon:    Lock,
    color:   "#0088cc",
    requiresAuth: true,
  },
  {
    type:   NOTIF_TYPES.FRIEND_REQUEST,
    labelAr: "طلبات الصداقة",
    labelEn: "Friend Requests",
    descAr:  "طلبات الصداقة الجديدة",
    descEn:  "New friend requests",
    icon:    Users,
    color:   "#a855f7",
    requiresAuth: true,
  },
  {
    type:   NOTIF_TYPES.FRIEND_ACCEPT,
    labelAr: "قبول الصداقة",
    labelEn: "Friend Accepted",
    descAr:  "عندما يقبل أحدهم طلب صداقتك",
    descEn:  "When someone accepts your request",
    icon:    UserCheck,
    color:   "#22c55e",
    requiresAuth: true,
  },
  {
    type:   NOTIF_TYPES.SUPPORT_REPLY,
    labelAr: "ردود الدعم",
    labelEn: "Support Replies",
    descAr:  "ردود فريق الدعم على استفساراتك",
    descEn:  "Support team replies to your tickets",
    icon:    HeadphonesIcon,
    color:   "#f59e0b",
    requiresAuth: false,
  },
  {
    type:   NOTIF_TYPES.PAYMENT_APPROVED,
    labelAr: "قبول الدفع",
    labelEn: "Payment Approved",
    descAr:  "إشعار عند قبول إثبات الدفع + كلمة السر",
    descEn:  "When payment proof is approved + password",
    icon:    ShoppingCart,
    color:   "#00ff88",
    requiresAuth: true,
  },
  {
    type:   NOTIF_TYPES.PAYMENT_REJECTED,
    labelAr: "رفض الدفع",
    labelEn: "Payment Rejected",
    descAr:  "إشعار عند رفض إثبات الدفع",
    descEn:  "When payment proof is rejected",
    icon:    ShoppingCart,
    color:   "#ff4444",
    requiresAuth: true,
  },
];

const NotificationPrompt = ({ currentUser, isAr }) => {
  const [visible, setVisible] = useState(false);
  const [step,    setStep]    = useState("main"); // main | customize | granted | blocked | blockedCustomize
  const [status,  setStatus]  = useState("idle"); // idle | loading | granted
  const [prefs,   setPrefs]   = useState(getNotifPrefs);
  const timerRef = useRef(null);
  const isGuest  = !currentUser || currentUser.isAnonymous;

  // ── التحقق هل يجب إظهار الرسالة ──────────────────────────────────────────
  useEffect(() => {
    if (!("Notification" in window)) return;

    // الإشعارات مفعّلة بالفعل — لا نعرض
    if (Notification.permission === "granted") return;

    let stored = null;
    try { stored = localStorage.getItem(STORAGE_KEY); } catch {}

    if (stored === "forever") return;

    if (stored) {
      try {
        const diffDays = (Date.now() - parseInt(stored, 10)) / (1000 * 60 * 60 * 24);
        if (diffDays < SNOOZE_DAYS) return;
      } catch {}
    }

    timerRef.current = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // ── حفظ الـ FCM token بعد منح الإذن (للأعضاء فقط) ────────────────────────
  const saveTokenAfterGrant = useCallback(async () => {
    if (!currentUser?.uid || currentUser.isAnonymous) return;
    try {
      await registerFCMServiceWorker();
      const token = await getFCMToken();
      if (token) {
        await set(ref(db, `fcm_tokens/${currentUser.uid}`), {
          token,
          updatedAt: Date.now(),
          userAgent: navigator.userAgent.slice(0, 120),
          platform:  navigator.platform || "web",
        });
      }
    } catch {}
  }, [currentUser]);

  // ── طلب إذن الإشعارات ────────────────────────────────────────────────────
  const handleAllow = useCallback(async () => {
    setStatus("loading");
    try {
      if (Notification.permission === "denied") {
        setStep("blocked");
        setStatus("idle");
        return;
      }

      const permission = await Notification.requestPermission();

      if (permission === "granted") {
        saveNotifPrefs(prefs);
        syncPrefsWithSW(prefs);
        setStatus("granted");
        setStep("granted");
        await saveTokenAfterGrant();
        setTimeout(() => setVisible(false), 2500);

      } else if (permission === "denied") {
        setStep("blocked");
        setStatus("idle");
        try { localStorage.setItem(STORAGE_KEY, Date.now().toString()); } catch {}

      } else {
        setStatus("idle");
      }
    } catch {
      setStatus("idle");
    }
  }, [prefs, saveTokenAfterGrant]);

  // ── انتقالات الشاشات ──────────────────────────────────────────────────────
  const handleCustomize  = useCallback(() => setStep("customize"), []);
  const handleBackToMain = useCallback(() => setStep("main"), []);

  // ── تبديل تفعيل نوع إشعار ────────────────────────────────────────────────
  const togglePref = useCallback((type) => {
    setPrefs((prev) => ({ ...prev, [type]: !prev[type] }));
  }, []);

  const handleToggleAll = useCallback((value) => {
    const updated = {};
    NOTIF_ITEMS.forEach((item) => { updated[item.type] = value; });
    setPrefs(updated);
  }, []);

  // ── إغلاق مؤقت (snooze 3 أيام) ──────────────────────────────────────────
  const handleClose = useCallback(() => {
    try { localStorage.setItem(STORAGE_KEY, Date.now().toString()); } catch {}
    setVisible(false);
  }, []);

  // ── إغلاق دائم ───────────────────────────────────────────────────────────
  const handleNeverShow = useCallback(() => {
    try { localStorage.setItem(STORAGE_KEY, "forever"); } catch {}
    setVisible(false);
  }, []);

  if (!visible) return null;

  // ══════════════════════════════════════════════════════════════════════════
  // شاشة: الإشعارات محظورة من المتصفح (مع تخصيص بعد التفعيل)
  // ══════════════════════════════════════════════════════════════════════════
  if (step === "blocked") {
    return (
      <>
        <div className="np-backdrop" onClick={handleClose} aria-hidden="true" />
        <div
          className={`np-container np-container--wide${isAr ? " np-container--rtl" : ""}`}
          role="dialog"
          aria-modal="true"
          aria-label={isAr ? "الإشعارات محظورة" : "Notifications Blocked"}
        >
          <button className="np-close-btn" onClick={handleClose} type="button" aria-label="Close">
            <X size={16} />
          </button>

          <div className="np-icon-wrap" aria-hidden="true">
            <div className="np-icon np-icon--blocked">
              <Shield size={28} />
            </div>
          </div>

          <h3 className="np-title np-title--blocked">
            {isAr ? "الإشعارات محظورة 🔒" : "Notifications Blocked 🔒"}
          </h3>
          <p className="np-body">
            {isAr
              ? "متصفحك يمنع الإشعارات حالياً. اتبع الخطوات التالية لتفعيلها:"
              : "Your browser is blocking notifications. Follow these steps to enable them:"}
          </p>

          <div className="np-blocked-steps">
            <div className="np-blocked-step">
              <span className="np-blocked-step-num">1</span>
              <span>
                {isAr
                  ? "اضغط على أيقونة القفل 🔒 أو النقاط ⋮ في شريط العنوان"
                  : "Click the lock 🔒 or menu ⋮ icon in the address bar"}
              </span>
            </div>
            <div className="np-blocked-step">
              <span className="np-blocked-step-num">2</span>
              <span>
                {isAr
                  ? 'اختر "إعدادات الموقع" أو "Site Settings"'
                  : 'Select "Site Settings" or "Permissions"'}
              </span>
            </div>
            <div className="np-blocked-step">
              <span className="np-blocked-step-num">3</span>
              <span>
                {isAr
                  ? 'غيّر "الإشعارات" من "محظور" إلى "سماح"'
                  : 'Change "Notifications" from "Block" to "Allow"'}
              </span>
            </div>
            <div className="np-blocked-step">
              <span className="np-blocked-step-num">4</span>
              <span>{isAr ? "أعد تحميل الصفحة" : "Reload the page"}</span>
            </div>
          </div>

          {/* ملاحظة: بعد إعادة التحميل يمكنك تخصيص الإشعارات */}
          <div className="np-blocked-hint">
            <Info size={13} />
            <span>
              {isAr
                ? "بعد إعادة التحميل ستظهر لك لوحة تخصيص الإشعارات"
                : "After reloading, a notification settings panel will appear"}
            </span>
          </div>

          <div className="np-actions np-actions--single">
            <button
              className="np-btn np-btn--allow"
              onClick={() => window.location.reload()}
              type="button"
            >
              <RefreshCw size={14} />
              {isAr ? "إعادة التحميل" : "Reload Page"}
            </button>
          </div>

          <button className="np-never-show" onClick={handleNeverShow} type="button">
            {isAr ? "لا تظهر هذه الرسالة مجدداً" : "Don't show this again"}
          </button>
        </div>
      </>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // شاشة: تم تفعيل الإشعارات بنجاح
  // ══════════════════════════════════════════════════════════════════════════
  if (step === "granted") {
    return (
      <>
        <div className="np-backdrop np-backdrop--fade" aria-hidden="true" />
        <div
          className={`np-container${isAr ? " np-container--rtl" : ""}`}
          role="dialog"
          aria-live="polite"
        >
          <div className="np-icon-wrap" aria-hidden="true">
            <div className="np-icon np-icon--success">
              <CheckCircle size={28} />
            </div>
          </div>
          <h3 className="np-title np-title--success">
            {isAr ? "تم تفعيل الإشعارات! 🎉" : "Notifications Enabled! 🎉"}
          </h3>
          <p className="np-body">
            {isAr
              ? "رائع! ستصلك الآن إشعارات فورية حسب تفضيلاتك. يمكنك تعديلها في أي وقت من زر الجرس 🔔"
              : "Great! You'll now receive instant notifications. You can adjust them anytime via the bell 🔔 button."}
          </p>
        </div>
      </>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // شاشة: تخصيص الإشعارات
  // ══════════════════════════════════════════════════════════════════════════
  if (step === "customize") {
    const allEnabled  = NOTIF_ITEMS.every((i) => prefs[i.type] !== false);
    const noneEnabled = NOTIF_ITEMS.every((i) => prefs[i.type] === false);

    return (
      <>
        <div className="np-backdrop" onClick={handleClose} aria-hidden="true" />
        <div
          className={`np-container np-container--wide${isAr ? " np-container--rtl" : ""}`}
          role="dialog"
          aria-modal="true"
          aria-label={isAr ? "تخصيص الإشعارات" : "Customize Notifications"}
        >
          <button className="np-close-btn" onClick={handleClose} type="button" aria-label="Close">
            <X size={16} />
          </button>

          <div className="np-icon-wrap" aria-hidden="true">
            <div className="np-icon np-icon--bell">
              <Settings size={24} />
              <span className="np-icon-pulse" aria-hidden="true" />
            </div>
          </div>

          <h3 className="np-title">
            {isAr ? "خصّص إشعاراتك 🔔" : "Customize Notifications 🔔"}
          </h3>
          <p className="np-body">
            {isAr
              ? "اختر أنواع الإشعارات التي تريد استقبالها:"
              : "Choose which notifications you want to receive:"}
          </p>

          {/* أزرار تفعيل / إيقاف الكل */}
          <div className="np-toggle-all-row">
            <button
              className={`np-toggle-all-btn${allEnabled ? " np-toggle-all-btn--active" : ""}`}
              onClick={() => handleToggleAll(true)}
              type="button"
            >
              {isAr ? "تفعيل الكل" : "Enable All"}
            </button>
            <button
              className={`np-toggle-all-btn${noneEnabled ? " np-toggle-all-btn--danger" : ""}`}
              onClick={() => handleToggleAll(false)}
              type="button"
            >
              {isAr ? "إيقاف الكل" : "Disable All"}
            </button>
          </div>

          {/* قائمة أنواع الإشعارات */}
          <div className="np-prefs-list">
            {NOTIF_ITEMS.map((item) => {
              const Icon    = item.icon;
              const enabled = prefs[item.type] !== false;
              const locked  = isGuest && item.requiresAuth;

              return (
                <button
                  key={item.type}
                  className={`np-pref-item${locked ? " np-pref-item--locked" : enabled ? " np-pref-item--on" : " np-pref-item--off"}`}
                  onClick={() => !locked && togglePref(item.type)}
                  type="button"
                  disabled={locked}
                  aria-pressed={enabled && !locked}
                >
                  <div
                    className="np-pref-icon"
                    style={{
                      color:       locked ? "rgba(255,255,255,0.2)" : (enabled ? item.color : "rgba(255,255,255,0.3)"),
                      borderColor: locked ? "rgba(255,255,255,0.06)" : (enabled ? `${item.color}44` : "rgba(255,255,255,0.08)"),
                    }}
                  >
                    {locked ? <Lock size={14} /> : <Icon size={16} />}
                  </div>
                  <div className="np-pref-info">
                    <span
                      className="np-pref-label"
                      style={{ color: locked ? "rgba(255,255,255,0.28)" : (enabled ? "#fff" : "rgba(255,255,255,0.45)") }}
                    >
                      {isAr ? item.labelAr : item.labelEn}
                    </span>
                    <span className="np-pref-desc">
                      {locked
                        ? (isAr ? "للأعضاء المسجلين فقط" : "For registered members only")
                        : (isAr ? item.descAr : item.descEn)}
                    </span>
                  </div>
                  <div
                    className={`np-pref-toggle${locked ? " np-pref-toggle--locked" : enabled ? " np-pref-toggle--on" : " np-pref-toggle--off"}`}
                    style={{
                      background: locked
                        ? "rgba(255,255,255,0.06)"
                        : enabled ? item.color : "rgba(255,255,255,0.12)",
                    }}
                  >
                    <div className="np-pref-toggle-knob" />
                  </div>
                </button>
              );
            })}
          </div>

          {/* ملاحظة الزوار */}
          {isGuest && (
            <div className="np-guest-hint">
              <Info size={12} />
              <span>
                {isAr
                  ? "بعض الإشعارات تتطلب تسجيل دخول"
                  : "Some notifications require login"}
              </span>
            </div>
          )}

          <div className="np-actions">
            <button
              className="np-btn np-btn--allow"
              onClick={handleAllow}
              disabled={status === "loading" || noneEnabled}
              type="button"
            >
              {status === "loading" ? (
                <span className="np-spinner" aria-label={isAr ? "جارٍ التفعيل..." : "Enabling..."} />
              ) : (
                <Bell size={15} />
              )}
              {status === "loading"
                ? (isAr ? "جارٍ التفعيل..." : "Enabling...")
                : (isAr ? "حفظ وتفعيل" : "Save & Enable")}
            </button>
            <button className="np-btn np-btn--later" onClick={handleBackToMain} type="button">
              {isAr ? "رجوع" : "Back"}
            </button>
          </div>

          <button className="np-never-show" onClick={handleNeverShow} type="button">
            {isAr ? "لا تظهر هذه الرسالة مجدداً" : "Don't show this again"}
          </button>
        </div>
      </>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // شاشة: الرئيسية
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <>
      <div className="np-backdrop" onClick={handleClose} aria-hidden="true" />
      <div
        className={`np-container${isAr ? " np-container--rtl" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={isAr ? "تفعيل الإشعارات" : "Enable Notifications"}
      >
        {/* زر الإغلاق */}
        <button
          className="np-close-btn"
          onClick={handleClose}
          type="button"
          aria-label={isAr ? "إغلاق" : "Close"}
        >
          <X size={16} />
        </button>

        {/* الأيقونة الرئيسية */}
        <div className="np-icon-wrap" aria-hidden="true">
          <div className="np-icon np-icon--bell">
            <Bell size={28} />
            <span className="np-icon-pulse" aria-hidden="true" />
          </div>
        </div>

        {/* العنوان والوصف */}
        <h3 className="np-title">
          {isAr ? "ابقَ على اطلاع دائم! 🔔" : "Stay Updated! 🔔"}
        </h3>
        <p className="np-body">
          {isAr
            ? "فعّل الإشعارات لتصلك تنبيهات فورية عن الرسائل، الأصدقاء، والمستجدات."
            : "Enable notifications to get instant alerts about messages, friends, and updates."}
        </p>

        {/* مزايا الإشعارات */}
        <ul className="np-features" aria-label={isAr ? "مزايا الإشعارات" : "Notification benefits"}>
          <li>
            <span className="np-feature-dot" style={{ background: "#e50000" }} aria-hidden="true" />
            {isAr ? "رسائل الشات العام والخاص" : "Global & private chat messages"}
          </li>
          <li>
            <span className="np-feature-dot" style={{ background: "#a855f7" }} aria-hidden="true" />
            {isAr ? "طلبات الصداقة وقبولها" : "Friend requests & acceptances"}
          </li>
          <li>
            <span className="np-feature-dot" style={{ background: "#f59e0b" }} aria-hidden="true" />
            {isAr ? "ردود فريق الدعم على استفساراتك" : "Support team replies to your tickets"}
          </li>
          <li>
            <span className="np-feature-dot" style={{ background: "#22c55e" }} aria-hidden="true" />
            {isAr ? "إشعارات تصل للهاتف حتى عند إغلاق الموقع" : "Phone notifications even when site is closed"}
          </li>
        </ul>

        {/* أزرار الإجراء */}
        <div className="np-actions">
          <button
            className="np-btn np-btn--allow"
            onClick={handleAllow}
            disabled={status === "loading"}
            type="button"
          >
            {status === "loading" ? (
              <span className="np-spinner" aria-label={isAr ? "جارٍ التفعيل..." : "Enabling..."} />
            ) : (
              <Bell size={15} />
            )}
            {status === "loading"
              ? (isAr ? "جارٍ التفعيل..." : "Enabling...")
              : (isAr ? "تفعيل الإشعارات" : "Enable Notifications")}
          </button>
          <button
            className="np-btn np-btn--later"
            onClick={handleClose}
            type="button"
          >
            {isAr ? "لاحقاً" : "Later"}
          </button>
        </div>

        {/* زر التخصيص */}
        <button
          className="np-customize-btn"
          onClick={handleCustomize}
          type="button"
        >
          <Settings size={13} />
          {isAr ? "تخصيص الإشعارات" : "Customize notifications"}
        </button>

        {/* رابط لا تظهر مرة أخرى */}
        <button
          className="np-never-show"
          onClick={handleNeverShow}
          type="button"
        >
          {isAr ? "لا تظهر هذه الرسالة مجدداً" : "Don't show this again"}
        </button>
      </div>
    </>
  );
};

export default NotificationPrompt;
